<script setup lang="ts">
import { VForm } from 'vuetify/components/VForm'

import { VNodeRenderer } from '@layouts/components/VNodeRenderer'
import { themeConfig } from '@themeConfig'
import { emailValidator, requiredValidator } from '@core/utils/validators'
import { useI18n } from 'vue-i18n'
import { useFeatureStore } from '@/stores/features'
import { useLanguageStore } from '@/stores/language'
import { getI18n } from '@/plugins/i18n'
import { usePlatformBranding } from '@/composables/usePlatformBranding'
import { phonePrefixOptions, type PhonePrefixOption } from '@/constants/phone-prefixes'
import I18n from '@core/components/I18n.vue'

import authV2RegisterIllustrationBorderedDark from '@images/pages/auth-v2-register-illustration-bordered-dark.png'
import authV2RegisterIllustrationBorderedLight from '@images/pages/auth-v2-register-illustration-bordered-light.png'
import authV2RegisterIllustrationDark from '@images/pages/auth-v2-register-illustration-dark.png'
import authV2RegisterIllustrationLight from '@images/pages/auth-v2-register-illustration-light.png'
import authV2MaskDark from '@images/pages/misc-mask-dark.png'
import authV2MaskLight from '@images/pages/misc-mask-light.png'

const imageVariant = useGenerateImageVariant(authV2RegisterIllustrationLight,
  authV2RegisterIllustrationDark,
  authV2RegisterIllustrationBorderedLight,
  authV2RegisterIllustrationBorderedDark, true)

const authThemeMask = useGenerateImageVariant(authV2MaskLight, authV2MaskDark)
const { brandName, logoUrl, authHeadline, authSubheadline, isWhitelabel, loadPlatformBranding, showPlatformLogoFallback } = usePlatformBranding()
const { t } = useI18n()
const registerHeadline = computed(() => authHeadline.value || t('auth.register_title'))
const registerSubheadline = computed(() => authSubheadline.value || t('auth.register_subtitle'))

definePage({
  meta: {
    layout: 'blank',
    unauthenticatedOnly: true,
  },
})

const form = ref({
  username: '',
  email: '',
  password: '',
  phonePrefix: (phonePrefixOptions.find(option => option.code === 'IT') ?? phonePrefixOptions[0]) as PhonePrefixOption,
  phoneNumber: '',
  privacyPolicies: false,
})

const isPasswordVisible = ref(false)
const loading = ref(false)
const serverErrors = ref<Record<string, string[]>>({})
const infoMessage = ref('')

const filterPhonePrefix = (_title: string, query: string, item?: { raw?: PhonePrefixOption }) => {
  const normalizedQuery = query.trim().toLowerCase()

  if (!normalizedQuery)
    return true

  const haystack = [
    item?.raw?.name ?? '',
    item?.raw?.dialCode ?? '',
    item?.raw?.code ?? '',
  ]
    .join(' ')
    .toLowerCase()

  return haystack.includes(normalizedQuery)
}

const ability = useAbility()
const router = useRouter()

const onSubmit = async () => {
  serverErrors.value = {}
  infoMessage.value = ''
  if (!form.value.privacyPolicies)
    return (serverErrors.value = { privacyPolicies: [t('register_page.privacy_policy_required')] })

  loading.value = true
  try {
    const tenantId = localStorage.getItem('tenantId') || undefined
    const headers: Record<string, string> = { 'X-Tenant-Domain': window.location.host }
    if (tenantId)
      headers['X-Tenant-ID'] = tenantId

    // Initialize CSRF cookie (Sanctum)
    await $api('/sanctum/csrf-cookie').catch(() => {})

    const res = await $api('/auth/register', {
      method: 'POST',
      headers,
      body: {
        name: form.value.username,
        email: form.value.email,
        password: form.value.password,
        phone_prefix: form.value.phonePrefix.dialCode,
        phone: form.value.phoneNumber,
        privacy_policies: form.value.privacyPolicies,
      },
      onResponseError({ response }) {
        const payload = response._data || {}

        serverErrors.value = (payload.errors as Record<string, string[]>) || { general: [payload.message || t('register_page.registration_error')] }
      },
    })

    if (res?.requires_email_verification) {
      // Il backend restituisce questo messaggio sempre in italiano
      // (AuthController::register()) - usiamo la traduzione lato frontend,
      // scegliendo tra le due varianti (email inviata/non inviata) in base
      // al flag, mai il testo grezzo della risposta.
      infoMessage.value = res.verification_email_sent
        ? t('register_page.verify_email_info')
        : t('register_page.verify_email_send_failed')

      return
    }

    const { accessToken, userData, userAbilityRules } = res

    // Salva tenantId se presente (importante per le API successive)
    const tid = userData?.tenant?.id
    if (tid) {
      try {
        localStorage.setItem('tenantId', String(tid))
        
        // Pulisci eventuale stato app precedente
        localStorage.removeItem('activeAppId')
        localStorage.removeItem('activeAppName')
      }
      catch (e) {
        console.error('Failed to save tenantId', e)
      }
    }

    useCookie('userAbilityRules').value = userAbilityRules
    ability.update(userAbilityRules)
    setUserDataCookie(userData)
    useCookie('accessToken').value = accessToken

    // Scaldiamo la cache di moduli/traduzioni prima del redirect, ma senza
    // bloccarlo: a questo punto la registrazione e' gia' andata a buon fine
    // (cookie gia' salvati sopra). Un tenant appena registrato non ha ancora
    // nessuna app: /features risponde 400 ("App context not resolved") e,
    // non essendoci un catch attorno a questo blocco, l'eccezione risaliva
    // non gestita e il redirect non scattava mai - il pulsante "Registrati"
    // sembrava non fare nulla (loading tornava a false nel finally) mentre
    // l'utente era gia' registrato e autenticato. Stesso bug e stessa causa
    // di login.vue, segnalato dall'utente il 2026-09-23.
    try {
      const featureStore = useFeatureStore()
      const languageStore = useLanguageStore()

      await featureStore.load()
      await languageStore.fetchTranslations(getI18n().global.locale.value)
    }
    catch (bootstrapError) {
      console.error('Post-register bootstrap (features/translations) failed, redirecting anyway', bootstrapError)
    }

    await router.push('/')
  }
  finally {
    loading.value = false
  }
}

onMounted(() => {
  loadPlatformBranding()
})
</script>

<template>
  <div class="auth-logo d-flex align-center gap-x-3">
    <Component
      :is="isWhitelabel ? 'div' : 'RouterLink'"
      :to="isWhitelabel ? undefined : '/'"
      class="d-flex align-center gap-x-3"
    >
      <img
        v-if="logoUrl"
        :src="logoUrl"
        :alt="brandName"
        style="display:block; max-width: 180px; max-height: 48px; width: auto; height: auto; object-fit: contain;"
      >
      <VNodeRenderer v-else-if="showPlatformLogoFallback" :nodes="themeConfig.app.logo" />
      <h1 class="auth-title">
        {{ brandName }}
      </h1>
    </Component>
  </div>

  <div class="auth-lang-selector">
    <I18n
      :languages="themeConfig.app.i18n.langConfig"
      location="bottom end"
    />
  </div>

  <VRow
    no-gutters
    class="auth-wrapper bg-surface"
  >
    <VCol
      md="8"
      class="d-none d-md-flex"
    >
      <div class="position-relative bg-background w-100 me-0">
        <div
          class="d-flex align-center justify-center w-100 h-100"
          style="padding-inline: 100px;"
        >
          <VImg
            max-width="500"
            :src="imageVariant"
            class="auth-illustration mt-16 mb-2"
          />
        </div>

        <img
          class="auth-footer-mask"
          :src="authThemeMask"
          alt="auth-footer-mask"
          height="280"
          width="100"
        >
      </div>
    </VCol>

    <VCol
      cols="12"
      md="4"
      class="auth-card-v2 d-flex align-center justify-center"
      style="background-color: rgb(var(--v-theme-surface));"
    >
      <VCard
        flat
        :max-width="500"
        class="mt-12 mt-sm-0 pa-4"
      >
        <VCardText>
          <h4 class="text-h4 mb-1">
            {{ registerHeadline }}
          </h4>
          <p class="mb-0">
            {{ registerSubheadline }}
          </p>
        </VCardText>

        <VCardText>
          <VForm @submit.prevent="onSubmit">
            <VRow>
              <!-- Name -->
              <VCol cols="12">
                <AppTextField
                  v-model="form.username"
                  :rules="[requiredValidator]"
                  autofocus
                  :label="$t('auth.full_name')"
                  :placeholder="$t('auth.full_name_placeholder')"
                  :error-messages="serverErrors.name"
                />
              </VCol>

              <!-- email -->
              <VCol cols="12">
                <AppTextField
                  v-model="form.email"
                  :rules="[requiredValidator, emailValidator]"
                  :label="$t('auth.email')"
                  type="email"
                  :error-messages="serverErrors.email"
                />
              </VCol>

              <!-- password -->
              <VCol cols="12">
                <AppTextField
                  v-model="form.password"
                  :rules="[requiredValidator, (v: string) => (String(v || '').length >= 8 ? true : $t('auth.password_hint'))]"
                  :label="$t('auth.password')"
                  :type="isPasswordVisible ? 'text' : 'password'"
                  autocomplete="password"
                  :append-inner-icon="isPasswordVisible ? 'tabler-eye-off' : 'tabler-eye'"
                  :error-messages="serverErrors.password"
                  @click:append-inner="isPasswordVisible = !isPasswordVisible"
                />

                <!-- phone prefix + number -->
                <div
                  class="d-flex flex-wrap"
                  style="gap:12px;"
                >
                  <AppAutocomplete
                    v-model="form.phonePrefix"
                    :items="phonePrefixOptions"
                    return-object
                    item-title="label"
                    item-value="code"
                    :label="$t('auth.phone_prefix')"
                    placeholder="+39"
                    style="max-width: 220px;"
                    :error-messages="serverErrors.phone_prefix"
                    :custom-filter="filterPhonePrefix"
                    no-filter
                  >
                    <template #selection="{ item }">
                      <div class="d-flex align-center gap-2">
                        <img
                          :src="item.raw.flagUrl"
                          :alt="`${item.raw.name} flag`"
                          class="phone-prefix-flag"
                        >
                        <span class="phone-prefix-selection">{{ item.raw.dialCode }}</span>
                      </div>
                    </template>
                    <template #item="{ props, item }">
                      <VListItem v-bind="props" :title="undefined" :subtitle="undefined">
                        <template #prepend>
                          <img
                            :src="item.raw.flagUrl"
                            :alt="`${item.raw.name} flag`"
                            class="phone-prefix-flag me-3"
                          >
                        </template>
                        <VListItemTitle>{{ item.raw.name }} ({{ item.raw.dialCode }})</VListItemTitle>
                        <VListItemSubtitle>{{ item.raw.code }}</VListItemSubtitle>
                      </VListItem>
                    </template>
                  </AppAutocomplete>
                  <AppTextField
                    v-model="form.phoneNumber"
                    :label="$t('auth.phone_number')"
                    :placeholder="$t('auth.phone_number_placeholder')"
                    :rules="[(v: string) => (!v || /^\d{5,15}$/.test(v) ? true : $t('auth.invalid_phone'))]"
                    style="flex:1; min-width: 220px;"
                    :error-messages="serverErrors.phone"
                  />
                </div>

                <div class="d-flex align-center my-6">
                  <VCheckbox
                    id="privacy-policy"
                    v-model="form.privacyPolicies"
                    inline
                  />
                  <VLabel
                    for="privacy-policy"
                    style="opacity: 1;"
                  >
                    <span class="me-1 text-high-emphasis">{{ $t('auth.accept') }}</span>
                    <a
                      href="javascript:void(0)"
                      class="text-primary"
                    >{{ $t('auth.privacy_policy_link') }}</a>
                  </VLabel>
                </div>
                <div
                  v-if="serverErrors.privacyPolicies"
                  class="mt-1 text-error text-body-2"
                >
                  {{ $t('auth.privacy_policy_error') }}
                </div>

                <VBtn
                  block
                  type="submit"
                  :loading="loading"
                >
                  {{ $t('auth.register') }}
                </VBtn>
                <div
                  v-if="serverErrors.general"
                  class="mt-2 text-error"
                >
                  {{ serverErrors.general?.[0] }}
                </div>
                <div
                  v-if="infoMessage"
                  class="mt-2 text-info"
                >
                  {{ infoMessage }}
                </div>
              </VCol>

              <!-- create account -->
              <VCol
                cols="12"
                class="text-center text-base"
              >
                <span class="d-inline-block">{{ $t('auth.already_have_account') }}</span>
                <RouterLink
                  class="text-primary ms-1 d-inline-block"
                  :to="{ name: 'login' }"
                >
                  {{ $t('auth.login_link') }}
                </RouterLink>
              </VCol>

              
            </VRow>
          </VForm>
        </VCardText>
      </VCard>
    </VCol>
  </VRow>
</template>

<style lang="scss">
@use "@core-scss/template/pages/page-auth";

.auth-lang-selector {
  position: absolute;
  inset-block-start: 2rem;
  inset-inline-end: 2.3rem;
  z-index: 2;
}

.phone-prefix-flag {
  display: inline-block;
  inline-size: 24px;
  block-size: 18px;
  object-fit: cover;
  border-radius: 3px;
  border: 1px solid rgba(var(--v-theme-on-surface), 0.12);
  background: rgba(var(--v-theme-on-surface), 0.04);
  flex-shrink: 0;
}

.phone-prefix-selection {
  font-weight: 600;
  letter-spacing: 0.01em;
}
</style>
